/**
 * Submitted revisions, sign-offs, review rounds and approved history.
 *
 * @module components/governance/GovernanceApprovalRecord
 */

import React from "react";

import { Alert, countLabel, Divider, Heading, StatCard, Text } from "@/components/ui";
import type {
  EditorProgrammeMetadata,
  ProgrammeGovernanceSummary,
  ProgrammeReviewRoundTimeline,
} from "@/platform";

import { ApprovalCandidate } from "./ApprovalCandidate";
import { CurriculumHistory } from "./CurriculumHistory";
import { GovernedRevisions } from "./GovernedRevisions";
import { LegacyAmendmentProposals } from "./LegacyAmendmentProposals";
import { ProgrammeLineage } from "./ProgrammeLineage";
import { ReviewRoundTimeline } from "./ReviewRoundTimeline";
import { RevisionApplicability } from "./RevisionApplicability";

export interface GovernanceApprovalRecordProps {
  governance: ProgrammeGovernanceSummary | null;
  programme: EditorProgrammeMetadata;
  reviewRounds: ProgrammeReviewRoundTimeline[];
  expanded: boolean;
  actionPending: boolean;
  leadSigned: boolean;
  qaSigned: boolean;
  submittedRevision: ProgrammeGovernanceSummary["governanceRevisions"][number] | undefined;
  approvedRevision: ProgrammeGovernanceSummary["governanceRevisions"][number] | undefined;
  approvalCandidate: ProgrammeGovernanceSummary["approvalCandidates"][number] | undefined;
  canSetApplicability: boolean;
  applicabilityRevisionId: string;
  effectiveFrom: string;
  onEffectiveFromChange: (value: string) => void;
  publicationConsent: boolean;
  onPublicationConsentChange: (value: boolean) => void;
  onSelectApplicabilityRevision: (governanceRevisionId: string) => void;
  onAddApplicability: () => Promise<void>;
  onRelationshipOpen?: (programmeId: string) => void;
  /** A user's name, from their id. */
  nameFor: (userId: string) => string;
}

export function GovernanceApprovalRecord({
  governance,
  programme,
  reviewRounds,
  expanded,
  actionPending,
  leadSigned,
  qaSigned,
  submittedRevision,
  approvedRevision,
  approvalCandidate,
  canSetApplicability,
  applicabilityRevisionId,
  effectiveFrom,
  onEffectiveFromChange,
  publicationConsent,
  onPublicationConsentChange,
  onSelectApplicabilityRevision,
  onAddApplicability,
  onRelationshipOpen = undefined,
  nameFor,
}: GovernanceApprovalRecordProps): React.ReactElement | null {
  if (!governance) {
    return null;
  }
  return (
    <div className="mt-4" data-testid="governance-approval-record" hidden={!expanded}>
      <div>
        <Heading level={3}>Approval record</Heading>
        <Text tone="muted" className="mt-1">
          See submitted revisions, sign-offs, review rounds and approved curriculum history.
        </Text>
      </div>
      <Divider className="mt-2 mb-3" />
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <StatCard label="Lead sign-off" value={leadSigned ? "Recorded" : "Pending"} />
        <StatCard label="QA sign-off" value={qaSigned ? "Recorded" : "Pending"} />
        <StatCard
          label="Provenance"
          value={
            <Text as="span" size="sm" className="whitespace-nowrap">
              {countLabel(governance.snapshots.length, "snapshot")} ·{" "}
              {countLabel(governance.exports.length, "export")}
            </Text>
          }
        />
      </div>

      <ApprovalCandidate approvalCandidate={approvalCandidate} nameFor={nameFor} />

      <GovernedRevisions
        governance={governance}
        submittedRevision={submittedRevision}
        approvedRevision={approvedRevision}
      />

      <CurriculumHistory governance={governance} />

      <ProgrammeLineage
        governance={governance}
        programme={programme}
        onRelationshipOpen={onRelationshipOpen}
      />

      <LegacyAmendmentProposals governance={governance} />

      <RevisionApplicability
        governance={governance}
        approvedRevision={approvedRevision}
        canSetApplicability={canSetApplicability}
        applicabilityRevisionId={applicabilityRevisionId}
        effectiveFrom={effectiveFrom}
        onEffectiveFromChange={onEffectiveFromChange}
        publicationConsent={publicationConsent}
        onPublicationConsentChange={onPublicationConsentChange}
        onSelectApplicabilityRevision={onSelectApplicabilityRevision}
        onAddApplicability={onAddApplicability}
        actionPending={actionPending}
      />

      <ReviewRoundTimeline
        reviewRounds={reviewRounds}
        workflowState={programme.workflowState}
        nameFor={nameFor}
      />

      {governance.revisionOf && governance.snapshotOf ? (
        <Alert tone="info" icon="git-branch" className="mt-4" data-testid="programme-lineage">
          Revised from a locked snapshot of an earlier programme.
        </Alert>
      ) : null}
    </div>
  );
}
